// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cx } from './cx';
import { moveEnabledIndex } from './segmented';
import styles from './SegmentedControl.module.css';

export interface SegmentedItem<T extends string | number> {
  value: T;
  /** The segment's text. With `iconOnly` it is visually hidden but stays the accessible name. */
  label: ReactNode;
  icon?: ReactNode;
  /** A count pill after the label (ModeTabs); null/empty for none. */
  badge?: string | null;
  /** Tooltip. Defaults to the label when `iconOnly` and the label is a string. */
  title?: string;
  /** `role="nav"` only: the route the segment links to. */
  href?: string;
  disabled?: boolean;
  /** The segment element's id (a tab, for its panel's `aria-labelledby`). */
  id?: string;
  /** `role="tablist"` only: the id of the panel the tab controls (`aria-controls`). */
  controls?: string;
}

export type SegmentedRole = 'radiogroup' | 'tablist' | 'group' | 'nav';

export interface SegmentedControlProps<T extends string | number> {
  items: readonly SegmentedItem<T>[];
  /** The chosen segment, or null for none. */
  value: T | null;
  /** Fires on every click, the active segment included (YesNo clears on a second press). */
  onChange?: (value: T) => void;
  'aria-label': string;
  /**
   * `radiogroup` (default): `role=radio` + `aria-checked`, roving tabindex, arrows/Home/End select.
   * `tablist`: `role=tab` + `aria-selected`/`aria-controls`, roving tabindex, arrows/Home/End select.
   * `group`: `aria-pressed` toggles, each its own tab stop, no arrow keys.
   * `nav`: a `<nav>` of router links (`item.href`), the current one `aria-current="page"`.
   */
  role?: SegmentedRole;
  /** Show only the icons; the labels stay for assistive tech. */
  iconOnly?: boolean;
  /** `sm` (default): 28px track. `md`: 32px track, a toolbar's bar height (Tabel's Typer/Dele). */
  size?: 'sm' | 'md';
  /** 44px segments (the phone drawer). */
  touch?: boolean;
  /** Toned for the navy chrome (ThemeToggle). */
  onChrome?: boolean;
  /** Scroll sideways (hidden scrollbar) rather than grow past the container. */
  scroll?: boolean;
  className?: string;
  /** Runs before the built-in arrow-key handling; `preventDefault()` skips it. */
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
}

/**
 * The shared segmented control: a tinted track whose chosen segment sits on a
 * raised chip. Generic over the value type; the role decides the semantics
 * (see `role`). Arrow-key movement lives in `segmented.ts` (`moveIndex`).
 */
export function SegmentedControl<T extends string | number>({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
  role = 'radiogroup',
  iconOnly,
  size = 'sm',
  touch,
  onChrome,
  scroll,
  className,
  onKeyDown,
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLElement | null)[]>([]);
  const roving = role === 'radiogroup' || role === 'tablist';
  const activeIndex = items.findIndex((it) => it.value === value);
  // The one tab stop of a roving group: the chosen segment, else the first enabled one.
  const stop = activeIndex >= 0 && !items[activeIndex].disabled ? activeIndex : items.findIndex((it) => !it.disabled);

  const trackClass = cx(
    styles.track,
    scroll && styles.scroll,
    iconOnly && styles.iconOnly,
    size === 'md' && styles.md,
    touch && styles.touch,
    onChrome && styles.onChrome,
    className,
  );

  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || !roving) return;
    // A horizontal tablist answers Left/Right only; a radiogroup all four arrows.
    if (role === 'tablist' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
    const focused = refs.current.findIndex((el) => el !== null && el === e.target);
    const at = focused >= 0 ? focused : activeIndex;
    const to = moveEnabledIndex(at, e.key, items.length, (i) => !!items[i].disabled);
    if (to < 0) return;
    e.preventDefault();
    e.stopPropagation();
    onChange?.(items[to].value);
    refs.current[to]?.focus();
  };

  const body = (item: SegmentedItem<T>) => (
    <>
      {item.icon != null && item.icon !== false && (
        <span className={styles.icon} aria-hidden="true">
          {item.icon}
        </span>
      )}
      <span className={iconOnly ? styles.srOnly : undefined}>{item.label}</span>
      {item.badge && <span className={styles.badge}>{item.badge}</span>}
    </>
  );

  const titleOf = (item: SegmentedItem<T>) =>
    item.title ?? (iconOnly && typeof item.label === 'string' ? item.label : undefined);

  if (role === 'nav') {
    return (
      <nav className={trackClass} aria-label={ariaLabel} onKeyDown={onKeyDown}>
        {items.map((item, i) => {
          const on = i === activeIndex;
          const cls = cx(styles.item, on && styles.on);
          if (item.disabled || item.href === undefined) {
            return (
              <span key={String(item.value)} id={item.id} className={cls} title={titleOf(item)} aria-disabled="true">
                {body(item)}
              </span>
            );
          }
          return (
            <Link
              key={String(item.value)}
              to={item.href}
              id={item.id}
              className={cls}
              title={titleOf(item)}
              aria-current={on ? 'page' : undefined}
              onClick={() => onChange?.(item.value)}
            >
              {body(item)}
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <div className={trackClass} role={role} aria-label={ariaLabel} onKeyDown={onKey}>
      {items.map((item, i) => {
        const on = i === activeIndex;
        return (
          <button
            key={String(item.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            id={item.id}
            className={cx(styles.item, on && styles.on)}
            title={titleOf(item)}
            disabled={item.disabled}
            role={role === 'radiogroup' ? 'radio' : role === 'tablist' ? 'tab' : undefined}
            aria-checked={role === 'radiogroup' ? on : undefined}
            aria-selected={role === 'tablist' ? on : undefined}
            aria-controls={role === 'tablist' ? item.controls : undefined}
            aria-pressed={role === 'group' ? on : undefined}
            tabIndex={roving ? (i === stop ? 0 : -1) : undefined}
            onClick={() => onChange?.(item.value)}
          >
            {body(item)}
          </button>
        );
      })}
    </div>
  );
}
